/**
 * The sidebar tree's data layer: the workspace root plus the children of
 * every expanded folder, flattened into the rows the tree draws (only what an
 * expanded path reaches). One query per expanded folder, each holding a
 * level's FIRST page. The levels are paged: a level with a next page ends in
 * a "Show more" entry, and the pages it loads are kept here, appended after
 * the first page (`useLevelPages`).
 *
 * Rows are adapted to `WorkspaceItem`, the shape the canvas's menu, drag and
 * drop builders take. A hierarchy node carries no per-item edit right, so
 * `canEdit` is the workspace role's (the legacy tree's rule too); the server
 * checks each item again.
 */

import { useCallback, useMemo } from 'react';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import { classifyFile, fileExtensionFromName } from '@bower/shared/constants/fileKinds';
import type { WorkspaceItem } from '@bower/shared';
import type { HierarchyNode } from '@bower/shared/types/api';

import { hierarchyChildrenQueryOptions, useHierarchyRoots } from '../../../hooks/data/useHierarchy';
import type { HierarchyChildrenPage } from '../../../services/api/hierarchy.api';
import { ROOT_LEVEL, useLevelPages, type Level, type ShowMoreEntry } from './useLevelPages';

type Path = ReadonlyArray<{ readonly id: string; readonly name: string }>;

export interface TreeRow {
  readonly node: HierarchyNode;
  readonly item: WorkspaceItem;
  /** 1-based, as `aria-level`. */
  readonly level: number;
  readonly posInSet: number;
  readonly setSize: number;
  /** The parent row's id, null at the root. */
  readonly parentId: string | null;
  readonly isFolder: boolean;
  readonly isExpanded: boolean;
}

export function toTreeItem(
  node: HierarchyNode,
  parentId: string | null,
  location: Path,
  canEdit: boolean,
): WorkspaceItem {
  const base = {
    id: node.id,
    name: node.name,
    parentId,
    location,
    privacyMode: node.privacyMode,
    canEdit,
    // Not on a hierarchy node; nothing the tree's menu or drag reads.
    updatedAt: '',
    createdAt: '',
    createdBy: null,
  };
  if (node.type === 'project') {
    return { ...base, kind: 'folder', childCount: node.childCount };
  }
  const artifactType = node.artifactType ?? 'note';
  const fileExtension = fileExtensionFromName(node.name);
  const { kind, technique } = classifyFile({ artifactType, mimeType: null, fileExtension });
  return {
    ...base,
    kind: 'file',
    artifactType,
    fileKind: kind,
    technique,
    fileExtension,
    mimeType: null,
    sizeBytes: null,
    thumbnailUrl: null,
    processingStatus: null,
  };
}

/** What the tree draws, in order: a row (by its index in `rows`) or a Show more. */
export type TreeEntry = { readonly kind: 'row'; readonly rowIndex: number } | ShowMoreEntry;

function flatten(
  root: Level,
  levelOf: ReadonlyMap<string, Level>,
  expanded: ReadonlySet<string>,
  canEdit: boolean,
): { rows: TreeRow[]; entries: TreeEntry[] } {
  const rows: TreeRow[] = [];
  const entries: TreeEntry[] = [];
  const walk = (level: Level, levelKey: string, parentId: string | null, path: Path) => {
    // A level with a next page has an unknown size (-1 per ARIA).
    const setSize = level.nextCursor ? -1 : level.nodes.length;
    level.nodes.forEach((node, index) => {
      const isFolder = node.type === 'project';
      const isExpanded = isFolder && expanded.has(node.id);
      entries.push({ kind: 'row', rowIndex: rows.length });
      rows.push({
        node,
        item: toTreeItem(node, parentId, path, canEdit),
        level: path.length + 1,
        posInSet: index + 1,
        setSize,
        parentId,
        isFolder,
        isExpanded,
      });
      const children = isExpanded ? levelOf.get(node.id) : undefined;
      if (children) walk(children, node.id, node.id, [...path, { id: node.id, name: node.name }]);
    });
    if (level.nextCursor) {
      entries.push({ kind: 'more', levelKey, level: path.length + 1, cursor: level.nextCursor });
    }
  };
  walk(root, ROOT_LEVEL, null, []);
  return { rows, entries };
}

export function useFolderTreeRows(expanded: ReadonlySet<string>, canEdit: boolean) {
  const { data: rootPage } = useHierarchyRoots();
  const expandedIds = useMemo(() => [...expanded], [expanded]);
  // Stable, so TanStack re-runs it (and the flatten below) only when a
  // child page actually changes, not on every render.
  const combine = useCallback(
    (results: ReadonlyArray<UseQueryResult<HierarchyChildrenPage>>) =>
      new Map(
        results.flatMap((result, index) =>
          result.data ? [[expandedIds[index] ?? '', result.data] as const] : [],
        ),
      ),
    [expandedIds],
  );
  const firstPages = useQueries({
    queries: expandedIds.map((id) => hierarchyChildrenQueryOptions('project', id)),
    combine,
  });
  const levels = useLevelPages(rootPage, firstPages);
  const { rows, entries } = useMemo(
    () => flatten(levels.root, levels.byFolder, expanded, canEdit),
    [levels, expanded, canEdit],
  );
  return { rows, entries, loadingMore: levels.loadingMore, showMore: levels.showMore };
}
